import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ExternalLinkIcon,
  PlusIcon,
  ChevronDownIcon,
  EyeIcon,
  Trash2Icon,
  XIcon,
  CameraIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ImagesIcon,
  PencilIcon,
  CalendarIcon,
  Share2Icon,
} from 'lucide-react';
import type {
  AgendaEvent,
  NieuwsPost,
  CreateNieuwsPostInput,
  SmoelenboekEntry,
} from '@kennisbank/shared';
import { roleIsAdmin } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { RichText } from '../components/RichText.js';
import { RichTextEditor } from '../components/RichTextEditor.js';
import { useAuth } from '../auth/AuthProvider.js';
import { apiDelete, apiGet, apiPost, apiPut } from '../api/client.js';
import { safeImageSrc } from '../lib/url.js';
import { shrink, toUploadable } from '../lib/image.js';
import { isUpcoming, MONTH_SHORT, parseDate } from '../lib/datum.js';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// `<input type="date">` werkt met YYYY-MM-DD, `publishedAt` is een ISO-tijdstip.
// Heen: de lokale dag (niet de UTC-dag, anders springt een bericht van 01:00 een
// dag terug). Terug: 12:00 lokaal, ver genoeg van middernacht dat geen enkele
// tijdzone-omrekening de dag nog verschuift.
function isoToDateInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dateInputToIso(date: string): string {
  return new Date(`${date}T12:00:00`).toISOString();
}

// ─── Photo upload helper ────────────────────────────────────────────────────
// Returns { stored, display }: `stored` is the value to persist (private GCS
// object path), `display` is a signed read URL for immediate preview. The
// bucket is private so the two differ. Albumfoto's (`withThumb`) krijgen er een
// miniatuur van 400 px bij voor het raster; de backend leidt de naam ervan af.
async function uploadPhoto(
  input: File,
  withThumb = false,
): Promise<{ stored: string; display: string }> {
  const file = await toUploadable(input, 2000);
  try {
    const { uploadUrl, thumbUploadUrl, imageObject, imageUrl } = await apiPost<{
      uploadUrl: string;
      thumbUploadUrl: string;
      imageObject: string;
      imageUrl?: string;
    }>('/api/v1/nieuws/photo-upload-url', { mimeType: file.type, sizeBytes: file.size });
    const put = async (url: string, body: File): Promise<void> => {
      const res = await fetch(url, { method: 'PUT', headers: { 'content-type': body.type }, body });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    };
    await put(uploadUrl, file);
    if (withThumb) {
      // Best effort: zonder miniatuur valt het raster terug op het origineel,
      // dus een mislukte miniatuur mag de foto zelf niet laten mislukken. De
      // URL is voor image/jpeg getekend; lukte het verkleinen niet, sla over.
      const thumb = await shrink(file, 400, 0.75);
      if (thumb.type === 'image/jpeg') await put(thumbUploadUrl, thumb).catch(() => undefined);
    }
    return { stored: imageObject, display: imageUrl ?? imageObject };
  } catch (err) {
    // Local dev ONLY: fall back to an in-memory data URL so the UI works without
    // GCS creds. In production a failed upload must surface as an error — never
    // masquerade as success (the data URL is not persisted, the image vanishes).
    if (import.meta.env.DEV) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      return { stored: dataUrl, display: dataUrl };
    }
    throw err;
  }
}

// Upload een reeks foto's voor het album. Vier tegelijk: 64 foto's één voor één
// duurt minutenlang, alle 64 tegelijk laat de browser de verbinding verdelen en
// levert niets op. De teller loopt per verwerkte foto.
// ponytail: vaste batch van 4; pas als iemand honderden foto's plaatst is een
// echte wachtrij met hervatten de moeite waard.
//
// allSettled i.p.v. Promise.all: één foto die de validatie niet haalt (te groot,
// verkeerd formaat) mag de andere 63 niet weggooien. De geslaagde uploads komen
// terug, het aantal mislukte apart, zodat de gebruiker weet wat er overgeslagen
// is zonder alles opnieuw te hoeven doen.
async function uploadPhotos(
  files: File[],
  onProgress: (done: number) => void,
): Promise<{ uploaded: { stored: string; display: string }[]; failed: number }> {
  const uploaded: { stored: string; display: string }[] = [];
  let failed = 0;
  for (let i = 0; i < files.length; i += 4) {
    const results = await Promise.allSettled(
      files.slice(i, i + 4).map((f) => uploadPhoto(f, true)),
    );
    for (const r of results) {
      if (r.status === 'fulfilled') uploaded.push(r.value);
      else failed += 1;
    }
    onProgress(uploaded.length + failed);
  }
  return { uploaded, failed };
}

// ─── Lightbox ───────────────────────────────────────────────────────────────
function Lightbox({
  photos,
  index,
  onIndex,
  onClose,
}: {
  photos: string[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    function handler(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex((index + 1) % photos.length);
      if (e.key === 'ArrowLeft') onIndex((index - 1 + photos.length) % photos.length);
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [index, photos.length, onIndex, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        aria-label="Sluiten"
      >
        <XIcon size={20} />
      </button>
      <button
        type="button"
        onClick={() => onIndex((index - 1 + photos.length) % photos.length)}
        className="absolute left-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        aria-label="Vorige foto"
      >
        <ChevronLeftIcon size={22} />
      </button>
      <img
        src={photos[index]}
        alt={`Foto ${index + 1} van ${photos.length}`}
        className="max-h-[85vh] max-w-full rounded-lg object-contain"
      />
      <button
        type="button"
        onClick={() => onIndex((index + 1) % photos.length)}
        className="absolute right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        aria-label="Volgende foto"
      >
        <ChevronRightIcon size={22} />
      </button>
      <span className="absolute bottom-5 text-[12px] font-medium text-white/70">
        {index + 1} / {photos.length}
      </span>
    </div>
  );
}

// ─── Article detail overlay ─────────────────────────────────────────────────
function ArticleDetail({
  post: listPost,
  canManage,
  onEdit,
  onClose,
}: {
  post: NieuwsPost;
  canManage: boolean;
  onEdit: (id: string) => void;
  onClose: () => void;
}): JSX.Element {
  // De lijst laat het album weg (te veel sign-calls), dus het volledige bericht
  // komt hier binnen. Mislukt dat, dan blijft de versie uit de lijst staan —
  // dan mist alleen het album, niet het artikel.
  const [post, setPost] = useState<NieuwsPost>(listPost);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [gedeeld, setGedeeld] = useState(false);

  // Alleen titel + link in het bericht; het linkvoorbeeld voegt datum en foto toe.
  async function deel(): Promise<void> {
    const text = `${post.title}\n${window.location.origin}/nieuws?item=${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        setGedeeld(true);
        setTimeout(() => setGedeeld(false), 2000);
      }
    } catch {
      // Gebruiker brak het deelvenster af; niets te melden.
    }
  }

  useEffect(() => {
    void apiGet<{ post: NieuwsPost }>(`/api/v1/nieuws/${listPost.id}?view=1`)
      .then((d) => setPost(d.post))
      .catch(() => undefined);
  }, [listPost.id]);

  // Neem een nieuwere versie van buiten over (na bewerken geeft de pagina het
  // opgeslagen bericht door), anders blijft hier de oude tekst staan.
  useEffect(() => setPost(listPost), [listPost]);

  // Close on Escape
  useEffect(() => {
    function handler(e: KeyboardEvent): void {
      if (e.key === 'Escape' && lightbox === null) onClose();
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, lightbox]);

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-white">
      {/* Back bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-brand-100 bg-white/95 px-5 py-3 backdrop-blur-sm">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 text-[13px] font-medium text-brand-700 hover:underline"
        >
          <ChevronLeftIcon size={15} />
          Nieuws
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void deel()}
            className="flex items-center gap-1.5 rounded-lg border border-brand-100 bg-white px-3 py-1.5 text-[12px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50"
          >
            <Share2Icon size={13} />
            {gedeeld ? 'Gekopieerd' : 'Delen'}
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => onEdit(post.id)}
              className="flex items-center gap-1.5 rounded-lg border border-brand-100 bg-white px-3 py-1.5 text-[12px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50"
            >
              <PencilIcon size={13} />
              Bewerken
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5 pb-16 pt-8">
        {/* Hero image */}
        {post.imageUrl && (
          <div className="mb-6 aspect-[16/7] w-full overflow-hidden rounded-2xl bg-brand-50">
            <img src={post.imageUrl} alt={post.title} className="h-full w-full object-cover" />
          </div>
        )}

        {/* Meta */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          {post.type === 'link' && (
            <span className="rounded-full bg-petrol-50 px-2.5 py-0.5 text-[11px] font-semibold text-petrol-700">
              Externe link
            </span>
          )}
          <span className="text-[13px] text-ink-900/50">
            {post.authorName} &middot; {formatDate(post.publishedAt)}
          </span>
          <span className="flex items-center gap-1 text-[12px] text-ink-900/35">
            <EyeIcon size={11} />
            {post.viewCount} weergaven
          </span>
        </div>

        {/* Title */}
        <h1 className="mb-5 text-[28px] font-bold leading-tight text-ink-900">{post.title}</h1>

        {/* Excerpt */}
        {post.excerpt && (
          <p className="mb-6 text-[16px] font-medium leading-relaxed text-ink-900/70">
            {post.excerpt}
          </p>
        )}

        {/* Content */}
        {post.content && (
          <RichText value={post.content} className="text-[15px] leading-relaxed text-ink-900" />
        )}

        {/* External link CTA */}
        {post.type === 'link' && post.linkUrl && (
          <a
            href={post.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-brand-700"
          >
            Lees het volledige artikel
            <ExternalLinkIcon size={15} />
          </a>
        )}

        {/* Fotoalbum */}
        {post.photos && post.photos.length > 0 && (
          <div className="mt-10">
            <h2 className="mb-3 text-[15px] font-bold text-ink-900">
              Foto&apos;s <span className="text-ink-900/40">({post.photos.length})</span>
            </h2>
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
              {post.photos.map((url, i) => (
                <button
                  key={url}
                  type="button"
                  onClick={() => setLightbox(i)}
                  className="aspect-square overflow-hidden rounded-lg bg-brand-50"
                >
                  {/* Het raster toont de miniatuur; het origineel laadt pas in
                      de lightbox. Ontbreekt de miniatuur, dan valt hij terug. */}
                  <img
                    src={post.photoThumbs?.[i] ?? url}
                    onError={(e) => {
                      if (e.currentTarget.src !== url) e.currentTarget.src = url;
                    }}
                    alt={`Foto ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-200 hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {lightbox !== null && post.photos && (
        <Lightbox
          photos={post.photos}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

// ─── Create / edit modal ────────────────────────────────────────────────────
function PostModal({
  post,
  initialType,
  onClose,
  onSaved,
}: {
  post?: NieuwsPost;
  initialType: 'post' | 'link';
  onClose: () => void;
  onSaved: (post: NieuwsPost) => void;
}): JSX.Element {
  const photoInputRef = useRef<HTMLInputElement>(null);
  const albumInputRef = useRef<HTMLInputElement>(null);
  const editing = post !== undefined;

  const [type, setType] = useState<'post' | 'link'>(post?.type ?? initialType);
  const [title, setTitle] = useState(post?.title ?? '');
  const [excerpt, setExcerpt] = useState(post?.excerpt ?? '');
  // `imageUrl` is de op te slaan verwijzing, `imagePreview` de toonbare URL. Bij
  // bewerken komt de verwijzing uit `imageRef`: `post.imageUrl` is een
  // ondertekende URL die na een uur verloopt en dus niet opgeslagen mag worden.
  const [imageUrl, setImageUrl] = useState(post?.imageRef ?? '');
  const [imagePreview, setImagePreview] = useState(post?.imageUrl ?? '');
  const [photos, setPhotos] = useState<{ stored: string; display: string }[]>(() =>
    (post?.photoRefs ?? []).map((ref, i) => ({
      stored: ref,
      display: post?.photoThumbs?.[i] ?? post?.photos?.[i] ?? ref,
    })),
  );
  const [content, setContent] = useState(post?.content ?? '');
  const [linkUrl, setLinkUrl] = useState(post?.linkUrl ?? '');
  const [status, setStatus] = useState<'published' | 'draft'>(post?.status ?? 'published');
  const [authorId, setAuthorId] = useState(post?.authorId ?? '');
  const initialDate = post
    ? isoToDateInput(post.publishedAt)
    : isoToDateInput(new Date().toISOString());
  const [publishedDate, setPublishedDate] = useState(initialDate);
  const [users, setUsers] = useState<SmoelenboekEntry[]>([]);
  const [uploading, setUploading] = useState(false);
  const [albumProgress, setAlbumProgress] = useState<{ done: number; total: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void apiGet<{ users: SmoelenboekEntry[] }>('/api/v1/users/smoelenboek')
      .then((d) => setUsers(d.users))
      .catch(() => undefined); // Geen lijst = het veld valt terug op de huidige auteur.
  }, []);

  async function handlePhotoSelect(file: File): Promise<void> {
    setUploading(true);
    setError('');
    try {
      const { stored, display } = await uploadPhoto(file);
      setImageUrl(stored);
      // Saneren bij het schrijven: state bevat zo nooit een onveilige URL,
      // ongeacht wat de upload-API teruggeeft.
      setImagePreview(safeImageSrc(display));
    } catch {
      setError('Foto uploaden mislukt. Probeer opnieuw.');
    } finally {
      setUploading(false);
    }
  }

  async function handleAlbumSelect(files: File[]): Promise<void> {
    const room = 100 - photos.length;
    if (room <= 0) {
      setError('Een bericht kan maximaal 100 foto’s bevatten.');
      return;
    }
    const batch = files.slice(0, room);
    setAlbumProgress({ done: 0, total: batch.length });
    setError('');
    try {
      const { uploaded, failed } = await uploadPhotos(batch, (done) =>
        setAlbumProgress({ done, total: batch.length }),
      );
      // Behoud wat wél lukte, ook als een deel faalde — nooit de hele reeks weggooien.
      if (uploaded.length > 0)
        setPhotos((prev) => [
          ...prev,
          ...uploaded.map((u) => ({ ...u, display: safeImageSrc(u.display) })),
        ]);
      const overLimit = files.length - batch.length; // boven de 100 per bericht
      const notes: string[] = [];
      if (failed > 0)
        notes.push(
          `${failed} foto${failed === 1 ? '' : "'s"} overgeslagen (te groot — max 25 MB — of geen JPEG/PNG/WebP)`,
        );
      if (overLimit > 0) notes.push(`${overLimit} boven de limiet van 100 per bericht`);
      setError(notes.join('. '));
    } catch {
      setError('Foto’s uploaden mislukt. Probeer het opnieuw.');
    } finally {
      setAlbumProgress(null);
    }
  }

  async function handleSubmit(): Promise<void> {
    if (!title.trim()) {
      setError('Vul een titel in.');
      return;
    }
    if (type === 'link' && !linkUrl.trim()) {
      setError('Vul een URL in voor een nieuwskoppeling.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const input: CreateNieuwsPostInput = {
        type,
        title: title.trim(),
        excerpt: excerpt.trim() || undefined,
        content: content.trim() || undefined,
        imageUrl: imageUrl.trim() || undefined,
        // Altijd de hele reeks, ook leeg: een weggelaten veld laat de backend het
        // oude album staan, `[]` haalt het weg.
        photos: photos.map((p) => p.stored),
        linkUrl: linkUrl.trim() || undefined,
        // Alleen meesturen als de poster echt gewijzigd is. Anders zou de server
        // de auteur bij elke bewerking opnieuw opzoeken — en een bericht van een
        // inmiddels verwijderde collega zou niet meer op te slaan zijn.
        authorId: (editing ? authorId !== post.authorId : Boolean(authorId)) ? authorId : undefined,
        // Alleen meesturen als de datum echt gewijzigd is: een ongemoeide datum
        // zou anders naar 12:00 verspringen en de volgorde van berichten van
        // dezelfde dag omgooien.
        publishedAt: publishedDate !== initialDate ? dateInputToIso(publishedDate) : undefined,
        status,
      };
      const data = editing
        ? await apiPut<{ post: NieuwsPost }>(`/api/v1/nieuws/${post.id}`, input)
        : await apiPost<{ post: NieuwsPost }>('/api/v1/nieuws', input);
      onSaved(data.post);
    } catch {
      setError('Opslaan mislukt. Probeer opnieuw.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex w-full max-w-lg flex-col gap-4 rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-bold text-ink-900">
            {editing
              ? 'Bericht bewerken'
              : type === 'post'
                ? 'Nieuwspost toevoegen'
                : 'Nieuwskoppeling toevoegen'}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-900/40 hover:text-ink-900">
            <XIcon size={18} />
          </button>
        </div>

        {/* Type toggle */}
        <div className="flex gap-2">
          {(['post', 'link'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`rounded-lg px-4 py-1.5 text-[12px] font-semibold transition-colors ${
                type === t
                  ? 'bg-brand-600 text-white'
                  : 'bg-brand-50 text-brand-700 hover:bg-brand-100'
              }`}
            >
              {t === 'post' ? 'Nieuwspost' : 'Nieuwskoppeling'}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          {/* Title */}
          <div>
            <label className="mb-1 block text-[12px] font-medium text-ink-900/70">Titel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titel van het bericht"
              className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="mb-1 block text-[12px] font-medium text-ink-900/70">
              Samenvatting <span className="text-ink-900/35">(optioneel)</span>
            </label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Korte introductietekst die op de kaart getoond wordt…"
              rows={2}
              className="w-full resize-none rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
          </div>

          {/* Photo upload */}
          <div>
            <label className="mb-1.5 block text-[12px] font-medium text-ink-900/70">
              Afbeelding <span className="text-ink-900/35">(optioneel)</span>
            </label>
            <div className="flex gap-2">
              {/* Preview */}
              {imagePreview || imageUrl ? (
                <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border border-brand-100 bg-brand-50">
                  <img
                    src={safeImageSrc(imagePreview || imageUrl)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImageUrl('');
                      setImagePreview('');
                    }}
                    className="absolute right-1 top-1 rounded-full bg-black/50 p-0.5 text-white hover:bg-black/70"
                  >
                    <XIcon size={10} />
                  </button>
                </div>
              ) : null}
              <div className="flex flex-1 flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-brand-200 bg-brand-50/50 py-2.5 text-[12px] font-medium text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50"
                >
                  <CameraIcon size={14} />
                  {uploading ? 'Uploaden…' : 'Foto uploaden'}
                </button>
                <input
                  type="text"
                  // Only show externally-pasted URLs here; an uploaded object
                  // path (or dev data: URL) is internal and stays hidden.
                  value={imageUrl.startsWith('http') ? imageUrl : ''}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    // Geplakte tekst pas als preview tonen zodra het een
                    // toegestane afbeeldings-URL is; tot die tijd ''.
                    setImagePreview(safeImageSrc(e.target.value));
                  }}
                  placeholder="Of plak een afbeelding-URL…"
                  className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-1.5 text-[12px] text-ink-900 placeholder:text-ink-900/30 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                />
              </div>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handlePhotoSelect(file);
              }}
            />
          </div>

          {/* Fotoalbum (alleen bij een nieuwspost — een koppeling is enkel een link) */}
          {type === 'post' && (
            <div>
              <label className="mb-1.5 block text-[12px] font-medium text-ink-900/70">
                Fotoalbum{' '}
                <span className="text-ink-900/35">
                  {photos.length > 0 ? `(${photos.length} foto's)` : '(optioneel)'}
                </span>
              </label>
              {photos.length > 0 && (
                <div className="mb-2 grid grid-cols-6 gap-1.5">
                  {photos.map((p, i) => (
                    <div
                      key={p.stored}
                      className="group relative aspect-square overflow-hidden rounded-md border border-brand-100 bg-brand-50"
                    >
                      <img
                        src={safeImageSrc(p.display)}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute right-0.5 top-0.5 rounded-full bg-black/50 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/70"
                        aria-label={`Foto ${i + 1} verwijderen`}
                      >
                        <XIcon size={9} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => albumInputRef.current?.click()}
                disabled={albumProgress !== null}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-brand-200 bg-brand-50/50 py-2.5 text-[12px] font-medium text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50"
              >
                <ImagesIcon size={14} />
                {albumProgress
                  ? `Uploaden… ${albumProgress.done} van ${albumProgress.total}`
                  : "Foto's toevoegen"}
              </button>
              <input
                ref={albumInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  // Leegmaken, anders geeft dezelfde selectie geen change-event meer.
                  e.target.value = '';
                  if (files.length > 0) void handleAlbumSelect(files);
                }}
              />
            </div>
          )}

          {/* Link URL (for type=link) */}
          {type === 'link' && (
            <div>
              <label className="mb-1 block text-[12px] font-medium text-ink-900/70">Link URL</label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://…"
                className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </div>
          )}

          {/* Content (for type=post) */}
          {type === 'post' && (
            <div>
              <label className="mb-1 block text-[12px] font-medium text-ink-900/70">
                Inhoud <span className="text-ink-900/35">(optioneel)</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Volledige tekst van het bericht…"
              />
              <p className="mt-1.5 text-[11px] text-ink-900/45">
                Selecteer tekst voor vet, cursief, lijsten en links.
              </p>
            </div>
          )}

          {/* Plaatsingsdatum + poster */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-ink-900/70">
                Plaatsingsdatum
              </label>
              <input
                type="date"
                value={publishedDate}
                onChange={(e) => setPublishedDate(e.target.value)}
                className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-ink-900/70">
                Geplaatst door
              </label>
              <select
                value={authorId}
                onChange={(e) => setAuthorId(e.target.value)}
                className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
              >
                {/* Bij bewerken kan de oorspronkelijke auteur inmiddels inactief
                    zijn en dus buiten de lijst vallen; die naam moet blijven staan. */}
                {editing && post && !users.some((u) => u.id === post.authorId) && (
                  <option value={post.authorId}>{post.authorName}</option>
                )}
                {!editing && <option value="">Mijzelf</option>}
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-3">
            <label className="text-[12px] font-medium text-ink-900/70">Status</label>
            <div className="flex gap-2">
              {(['published', 'draft'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`rounded-lg px-3 py-1 text-[12px] font-medium transition-colors ${
                    status === s
                      ? 'bg-brand-600 text-white'
                      : 'bg-brand-50 text-brand-700 hover:bg-brand-100'
                  }`}
                >
                  {s === 'published' ? 'Gepubliceerd' : 'Concept'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="text-[12px] text-error">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-[13px] text-ink-900/60 hover:bg-brand-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving || uploading || albumProgress !== null}
            className="rounded-xl bg-brand-600 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'Opslaan…' : editing ? 'Opslaan' : 'Publiceren'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tegel ──────────────────────────────────────────────────────────────────
// Beeldtegel met titel-overlay in een mozaïekraster; het nieuwste bericht
// krijgt een 2x2-tegel. Gekozen uit drie prototypes (PR #163, sept 2026);
// bewuste afwijking van de "grid-disciplined" layoutregel, zie DESIGN.md.
function NieuwsTile({
  post,
  featured,
  canManage,
  onOpen,
  onEdit,
  onDelete,
}: {
  post: NieuwsPost;
  featured: boolean;
  canManage: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <article
      className={`group relative cursor-pointer overflow-hidden rounded-lg bg-ink-900 ${
        featured ? 'row-span-2 sm:col-span-2' : ''
      }`}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // Alleen de tegel zelf; Enter op de bewerk-/verwijderknop mag het
        // bericht niet óók openen.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {post.imageUrl ? (
        <img
          src={post.imageUrl}
          alt={post.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-donker-aqua">
          <span className="text-6xl font-black text-white/20">
            {post.title.charAt(0).toUpperCase()}
          </span>
        </div>
      )}
      {/* Verloop zodat witte tekst leesbaar blijft op elke foto (ook lichte). */}
      <div className="absolute inset-0 bg-gradient-to-t from-ink-900/95 via-ink-900/70 to-ink-900/10" />

      <div className="absolute right-2 top-2 flex items-center gap-1">
        {/* Zonder deze telling is een album alleen te ontdekken door het bericht
            te openen — de tegel toont maar één afbeelding. */}
        {post.photoCount !== undefined && post.photoCount > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-semibold text-white">
            <ImagesIcon size={10} />
            {post.photoCount}
          </span>
        )}
        {post.type === 'link' && (
          <span className="rounded-full bg-black/50 p-1">
            <ExternalLinkIcon size={11} className="text-white" />
          </span>
        )}
        {canManage && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="rounded-full bg-black/50 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:bg-brand-600"
              title="Bericht bewerken"
            >
              <PencilIcon size={12} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!confirmDelete) {
                  setConfirmDelete(true);
                  return;
                }
                onDelete(post.id);
              }}
              onBlur={() => setConfirmDelete(false)}
              className={`rounded-full p-1.5 text-[11px] font-medium text-white transition-opacity ${
                confirmDelete
                  ? 'bg-error px-2.5'
                  : 'bg-black/50 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-error'
              }`}
              title={confirmDelete ? 'Klik nogmaals om te verwijderen' : 'Verwijder bericht'}
            >
              {confirmDelete ? 'Zeker?' : <Trash2Icon size={12} />}
            </button>
          </>
        )}
      </div>

      <div className="absolute inset-x-0 bottom-0 p-4">
        <h2
          className={`font-bold leading-snug text-white ${
            featured ? 'text-[24px]' : 'line-clamp-2 text-[15px]'
          }`}
        >
          {post.title}
        </h2>
        {featured && post.excerpt && (
          <p className="mt-1 line-clamp-2 text-[13px] text-white/80">{post.excerpt}</p>
        )}
        {/* Alleen de naam mag inkorten; de datum blijft altijd heel. */}
        <p className="mt-1.5 flex items-center gap-1 text-[12px] text-white/80">
          <span className="min-w-0 truncate">{post.authorName}</span>
          <span className="shrink-0">&middot; {formatDate(post.publishedAt)}</span>
          <span
            className="flex shrink-0 items-center gap-1"
            title="weergaven"
            aria-label="weergaven"
          >
            <EyeIcon size={11} />
            {post.viewCount}
          </span>
        </p>
      </div>
    </article>
  );
}

// ─── Agenda-tegel ────────────────────────────────────────────────────────────
// Vult het raster met wat er aankomt, zodat de pagina niet leeg oogt bij weinig
// berichten en een reden geeft om terug te komen.
function AgendaTile(): JSX.Element {
  const [events, setEvents] = useState<AgendaEvent[] | null>([]);

  useEffect(() => {
    void apiGet<{ events: AgendaEvent[] }>('/api/v1/agenda')
      .then((d) => setEvents(d.events.filter((e) => isUpcoming(e.date)).slice(0, 3)))
      .catch(() => setEvents(null));
  }, []);

  return (
    <Link
      to="/agenda"
      className="flex flex-col overflow-hidden rounded-lg bg-brand-600 p-4 text-white transition-colors hover:bg-brand-700 sm:col-span-2"
    >
      <h2 className="mb-2 flex items-center gap-2 text-[14px] font-semibold">
        <CalendarIcon size={16} /> Binnenkort
      </h2>
      {events === null ? (
        <p className="text-[13px] text-white/80">Agenda niet beschikbaar. Bekijk de agenda.</p>
      ) : events.length === 0 ? (
        <p className="text-[13px] text-white/80">Geen aankomende evenementen. Bekijk de agenda.</p>
      ) : (
        <ul className="flex flex-1 flex-col justify-around">
          {events.map((e) => {
            const { day, month } = parseDate(e.date);
            // `time` is vrije tekst; alleen een echte begintijd tonen.
            const start = /^\d{1,2}[:.]\d{2}/.exec(e.time)?.[0];
            return (
              <li key={e.id} className="flex items-baseline gap-3 text-[13px]">
                <span className="w-14 shrink-0 font-bold tabular-nums">
                  {day} {MONTH_SHORT[month]}
                </span>
                <span className="truncate">{e.title}</span>
                {start && <span className="ml-auto shrink-0 text-white/70">{start}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </Link>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export function NieuwsPage(): JSX.Element {
  const { user } = useAuth();
  // Alleen beheerders plaatsen nieuws (besluit Jos 10 sep 2026), in hun eigen
  // tenant (ADR 0018); de backend dwingt dit ook af met requireTenantAdmin.
  const canManage = user ? roleIsAdmin(user.role) : false;

  const [posts, setPosts] = useState<NieuwsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalType, setModalType] = useState<'post' | 'link' | null>(null);
  const [editPost, setEditPost] = useState<NieuwsPost | null>(null);
  // Het geopende bericht staat in de URL (`?item=<id>`) zodat de link deelbaar
  // is en direct het juiste bericht opent.
  const [params, setParams] = useSearchParams();
  const selectedPost = posts.find((p) => p.id === params.get('item')) ?? null;
  const setSelectedPost = (post: NieuwsPost | null): void =>
    setParams(post ? { item: post.id } : {}, { replace: true });
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void apiGet<{ posts: NieuwsPost[] }>('/api/v1/nieuws')
      .then((d) => setPosts(d.posts))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    function handler(e: MouseEvent): void {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleSaved(post: NieuwsPost): void {
    setPosts((prev) => {
      const known = prev.some((p) => p.id === post.id);
      const next = known ? prev.map((p) => (p.id === post.id ? post : p)) : [post, ...prev];
      // De lijst is op datum gesorteerd en de datum is aanpasbaar, dus opnieuw
      // sorteren — anders staat een teruggedateerd bericht bovenaan tot de
      // volgende keer laden.
      return [...next].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
    });
    setModalType(null);
    setEditPost(null);
    setSelectedPost(post);
  }

  // Bewerken werkt altijd op het volledig opgehaalde bericht: de lijst laat de
  // opslagverwijzingen van de foto's weg, en zonder die zou opslaan de
  // afbeelding en het album van het bericht wissen.
  async function openEdit(id: string): Promise<void> {
    const data = await apiGet<{ post: NieuwsPost }>(`/api/v1/nieuws/${id}`);
    setEditPost(data.post);
  }

  async function handleDelete(id: string): Promise<void> {
    await apiDelete(`/api/v1/nieuws/${id}`);
    setPosts((prev) => prev.filter((p) => p.id !== id));
  }

  function openCreate(type: 'post' | 'link'): void {
    setDropdownOpen(false);
    setModalType(type);
  }

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-5 py-8">
        {/* Kopregel: titel (niet op mobiel, staat al in de Layout-header),
            knop en teller op één regel zodat niets verspringt. */}
        <div className="mb-6 flex items-center gap-4" ref={dropdownRef}>
          <h1 className="hidden text-[22px] font-bold text-ink-900 sm:block">Nieuws</h1>
          {canManage && (
            <div className="relative inline-block">
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                className="flex items-center gap-1.5 rounded-lg border border-brand-100 bg-white px-3 py-1.5 text-[13px] font-medium text-ink-900/70 shadow-sm transition-colors hover:bg-brand-50"
              >
                <PlusIcon size={14} />
                Toevoegen
                <ChevronDownIcon
                  size={13}
                  className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {dropdownOpen && (
                <div className="absolute left-0 top-full z-20 mt-1 min-w-[180px] rounded-xl border border-brand-100 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => openCreate('post')}
                    className="w-full px-4 py-2.5 text-left text-[13px] text-ink-900 hover:bg-brand-50"
                  >
                    Nieuwspost
                  </button>
                  <button
                    type="button"
                    onClick={() => openCreate('link')}
                    className="w-full px-4 py-2.5 text-left text-[13px] text-ink-900 hover:bg-brand-50"
                  >
                    Nieuwskoppeling
                  </button>
                </div>
              )}
            </div>
          )}
          <span className="ml-auto text-[13px] font-medium text-brand-700">
            {posts.length > 0 ? `${posts.length} berichten` : ''}
          </span>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-16 text-center text-[13px] text-ink-900/40">Laden…</div>
        ) : loadError ? (
          <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
            <p className="text-[14px] font-medium text-error">
              Kon het nieuws niet laden. Ververs de pagina om het opnieuw te proberen.
            </p>
          </div>
        ) : (
          <div className="grid auto-rows-[190px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {posts.length === 0 && (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-brand-200 text-center sm:col-span-2">
                <p className="text-[14px] font-medium text-ink-900/40">Nog geen nieuwsberichten.</p>
                {canManage && (
                  <p className="mt-1 text-[12px] text-ink-900/30">
                    Klik op "+ Toevoegen" om het eerste bericht te plaatsen.
                  </p>
                )}
              </div>
            )}
            {posts.map((post, i) => (
              // Agenda-tegel vast op de tweede plek, anders zakt hij bij een
              // lange lijst naar de bodem.
              <Fragment key={post.id}>
                <NieuwsTile
                  post={post}
                  featured={i === 0}
                  canManage={canManage}
                  onOpen={() => setSelectedPost(post)}
                  onEdit={() => void openEdit(post.id)}
                  onDelete={(id) => void handleDelete(id)}
                />
                {i === 0 && <AgendaTile />}
              </Fragment>
            ))}
            {posts.length === 0 && <AgendaTile />}
            {canManage && (
              <button
                type="button"
                onClick={() => openCreate('post')}
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-brand-200 text-brand-700 transition-colors hover:bg-brand-50"
              >
                <PlusIcon size={24} />
                <span className="text-[13px] font-semibold">Bericht toevoegen</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {(modalType !== null || editPost) && (
        <PostModal
          {...(editPost ? { post: editPost } : {})}
          initialType={modalType ?? 'post'}
          onClose={() => {
            setModalType(null);
            setEditPost(null);
          }}
          onSaved={handleSaved}
        />
      )}
      {selectedPost && (
        <ArticleDetail
          post={selectedPost}
          canManage={canManage}
          onEdit={(id) => void openEdit(id)}
          onClose={() => setSelectedPost(null)}
        />
      )}
    </Layout>
  );
}
